import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { MessageSquare, Calendar, Send, CheckCircle, Loader2 } from "lucide-react";
import { CustomerAppointmentBooking } from "@/components/CustomerAppointmentBooking";
import { supabase } from "@/integrations/supabase/client";

export default function WidgetEmbed() {
  const [activeTab, setActiveTab] = useState("message");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    message: ""
  });

  const handleMessageSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.phone || !formData.message) return;

    setLoading(true);
    try {
      const { error } = await supabase.functions.invoke('widget-submit', {
        body: {
          name: formData.name,
          phone: formData.phone,
          message: formData.message
        }
      });

      if (error) throw error;
      setSubmitted(true);
    } catch (err) {
      console.error('Widget submit error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-[400px] flex items-center justify-center p-6">
        <div className="text-center space-y-4">
          <CheckCircle className="h-12 w-12 text-green-500 mx-auto" />
          <h3 className="text-lg font-semibold">Message Sent!</h3>
          <p className="text-sm text-muted-foreground">
            We&apos;ll get back to you shortly.
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setSubmitted(false);
              setFormData({ name: "", phone: "", message: "" });
            }}
          >
            Send Another
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[400px] bg-background">
      <div className="p-4 border-b bg-gradient-to-r from-primary/10 to-accent/10">
        <h2 className="font-semibold text-lg">iQ Texting</h2>
        <p className="text-sm text-muted-foreground">How can we help you today?</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="w-full grid grid-cols-2 rounded-none border-b">
          <TabsTrigger value="message" className="rounded-none">
            <MessageSquare className="h-4 w-4 mr-2" />
            Message
          </TabsTrigger>
          <TabsTrigger value="appointment" className="rounded-none">
            <Calendar className="h-4 w-4 mr-2" />
            Book Appointment
          </TabsTrigger>
        </TabsList>

        <TabsContent value="message" className="p-4 m-0">
          <form onSubmit={handleMessageSubmit} className="space-y-3">
            <div className="space-y-1">
              <Label htmlFor="widget-name" className="text-sm">Name</Label>
              <Input
                id="widget-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Your name"
                required
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="widget-phone" className="text-sm">Phone</Label>
              <Input
                id="widget-phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="+1234567890"
                required
              />
              <p className="text-xs text-muted-foreground">International format with +</p>
            </div>
            <div className="space-y-1">
              <Label htmlFor="widget-message" className="text-sm">Message</Label>
              <Textarea
                id="widget-message"
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                placeholder="How can we help?"
                rows={3}
                required
              />
            </div>
            <Button type="submit" disabled={loading} className="w-full">
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              <Send className="h-4 w-4 mr-2" />
              Send Message
            </Button>
          </form>
        </TabsContent>

        <TabsContent value="appointment" className="p-4 m-0">
          <CustomerAppointmentBooking
            onSuccess={() => setActiveTab("message")}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
